<template>
  <div class="electrical-sidebar column full-height" style="position: relative">
    <!-- Equipment Details -->
    <template v-if="selectedEquipment">
      <!-- Summary Card (Fixed Top) -->
      <q-card flat bordered class="equipment-summary-card">
        <q-card-section class="bg-primary text-white q-pa-sm">
          <div class="row items-center no-wrap">
            <q-icon :name="getEquipmentIcon(selectedEquipment.brick_class)" size="24px" class="q-mr-sm" />
            <div class="column">
              <div class="text-subtitle2 text-weight-bold">{{ selectedEquipment.name }}</div>
              <div class="text-caption">{{ formatBrickClass(selectedEquipment.brick_class) }}</div>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section v-if="loading" class="q-pa-md text-center">
          <q-spinner color="primary" size="40px" />
          <div class="text-caption text-grey-7 q-mt-sm">Loading details...</div>
        </q-card-section>

        <q-card-section v-else class="q-pa-sm">
          <div class="text-body2">
            <!-- Basic Info -->
            <div class="stat-row">
              <q-icon name="category" size="18px" class="q-mr-xs text-grey-7" />
              <span class="text-grey-8">Type:</span>
              <span class="text-weight-medium q-ml-auto">{{ getEquipmentType(selectedEquipment.brick_class) }}</span>
            </div>

            <!-- Editable Supply Information (for switchboards) -->
            <template v-if="isSwitchboard">
              <div class="stat-row">
                <q-icon name="bolt" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Rated:</span>
                <q-input
                  v-model="editableProps.rated_capacity"
                  dense
                  borderless
                  type="number"
                  suffix="A"
                  class="q-ml-auto text-weight-bold"
                  style="max-width: 100px"
                  @blur="updateEquipmentProperty('rated_capacity', editableProps.rated_capacity)"
                />
              </div>

              <div class="stat-row">
                <q-icon name="electric_bolt" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Voltage:</span>
                <q-input
                  v-model="editableProps.voltage"
                  dense
                  borderless
                  type="number"
                  suffix="V"
                  class="q-ml-auto text-weight-bold"
                  style="max-width: 100px"
                  @blur="updateEquipmentProperty('voltage', editableProps.voltage)"
                />
              </div>

              <div class="stat-row">
                <q-icon name="settings_input_component" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Phase:</span>
                <q-select
                  v-model="editableProps.phase_configuration"
                  :options="['Single Phase', 'Three Phase']"
                  dense
                  borderless
                  class="q-ml-auto text-weight-bold"
                  style="max-width: 150px"
                  @update:model-value="updateEquipmentProperty('phase_configuration', editableProps.phase_configuration)"
                />
              </div>

              <div class="stat-row">
                <q-icon name="business" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Manufacturer:</span>
                <q-input
                  v-model="editableProps.manufacturer"
                  dense
                  borderless
                  class="q-ml-auto text-caption"
                  style="max-width: 150px"
                  @blur="updateEquipmentProperty('manufacturer', editableProps.manufacturer)"
                />
              </div>

              <div class="stat-row">
                <q-icon name="label" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Model:</span>
                <q-input
                  v-model="editableProps.model_number"
                  dense
                  borderless
                  class="q-ml-auto text-caption"
                  style="max-width: 150px"
                  @blur="updateEquipmentProperty('model_number', editableProps.model_number)"
                />
              </div>
            </template>

            <!-- Read-only display for non-switchboards -->
            <template v-else>
              <div v-if="equipmentProps.rated_capacity || equipmentProps.amperage" class="stat-row">
                <q-icon name="bolt" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Rated:</span>
                <span class="text-weight-bold q-ml-auto">{{ equipmentProps.rated_capacity || equipmentProps.amperage }}A</span>
              </div>

              <div v-if="equipmentProps.voltage" class="stat-row">
                <q-icon name="electric_bolt" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Voltage:</span>
                <span class="text-weight-bold q-ml-auto">{{ equipmentProps.voltage }}V</span>
              </div>

              <div v-if="equipmentProps.phase_configuration" class="stat-row">
                <q-icon name="settings_input_component" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Phase:</span>
                <span class="text-weight-bold q-ml-auto">{{ equipmentProps.phase_configuration }}</span>
              </div>

              <div v-if="equipmentProps.manufacturer" class="stat-row">
                <q-icon name="business" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Manufacturer:</span>
                <span class="q-ml-auto text-caption">{{ equipmentProps.manufacturer }}</span>
              </div>

              <div v-if="equipmentProps.model_number" class="stat-row">
                <q-icon name="label" size="18px" class="q-mr-xs text-grey-7" />
                <span class="text-grey-8">Model:</span>
                <span class="q-ml-auto text-caption">{{ equipmentProps.model_number }}</span>
              </div>
            </template>

            <q-separator class="q-my-sm" />

            <!-- Meter Stats (for switchboards) -->
            <div v-if="isSwitchboard" class="stat-row">
              <q-icon name="speed" size="18px" class="q-mr-xs text-grey-7" />
              <span class="text-grey-8">Meters:</span>
              <span class="text-weight-bold q-ml-auto">{{ meterCount }}</span>
            </div>

            <!-- Feeds Count (for switchboards) -->
            <div v-if="isSwitchboard && feedsCount > 0" class="stat-row">
              <q-icon name="electric_bolt" size="18px" class="q-mr-xs text-grey-7" />
              <span class="text-grey-8">Feeds:</span>
              <span class="text-weight-bold q-ml-auto">{{ feedsCount }} equipment</span>
            </div>

            <!-- Controller Count (for DDC panels) -->
            <div v-if="isDDCPanel && controllerCount > 0" class="stat-row">
              <q-icon name="settings_remote" size="18px" class="q-mr-xs text-grey-7" />
              <span class="text-grey-8">Controllers:</span>
              <span class="text-weight-bold q-ml-auto">{{ controllerCount }}</span>
            </div>

            <!-- Monitoring Info (for meters) -->
            <div v-if="isMeter" class="stat-row">
              <q-icon name="visibility" size="18px" class="q-mr-xs text-grey-7" />
              <span class="text-grey-8">Monitoring:</span>
              <span class="q-ml-auto text-caption">{{ getMonitoringTarget() }}</span>
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- Scrollable Details Section -->
      <div class="col overflow-auto q-mt-sm">
        <q-list v-if="!loading" bordered separator>

          <!-- Meters Section (for switchboards) -->
          <q-expansion-item
            v-if="isSwitchboard"
            :label="`Meters (${meterCount})`"
            icon="speed"
            header-class="bg-green text-white"
            expand-icon-class="text-white"
            :default-opened="meterCount > 0"
          >
            <q-card>
              <q-card-section v-if="meterCount === 0" class="text-grey-7 text-center">
                No meters configured
              </q-card-section>
              <q-list v-else dense separator>
                <q-item v-for="meter in meters" :key="`meter-${meter.id}`" dense clickable @click="editMeter(meter)">
                  <q-item-section avatar>
                    <q-icon name="speed" color="green" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ meter.name }}</q-item-label>
                    <q-item-label caption>{{ formatBrickClass(meter.brick_class) }}</q-item-label>
                    <q-item-label v-if="getEquipmentProperties(meter).model_number" caption class="text-grey-6">
                      {{ getEquipmentProperties(meter).manufacturer }} {{ getEquipmentProperties(meter).model_number }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-btn
                      flat
                      dense
                      round
                      icon="delete"
                      color="negative"
                      size="sm"
                      @click.stop="deleteMeter(meter)"
                    >
                      <q-tooltip>Delete Meter</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </q-item>
              </q-list>

              <q-separator />

              <!-- Add Meter Button -->
              <q-card-section class="q-pa-sm">
                <q-btn
                  flat
                  dense
                  color="primary"
                  icon="add"
                  label="Add Meter"
                  class="full-width"
                  @click="addMeter"
                />
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <!-- Power Distribution Section (for switchboards) -->
          <q-expansion-item
            v-if="isSwitchboard"
            :label="`Power Distribution (${feedsCount})`"
            icon="electric_bolt"
            header-class="bg-amber text-white"
            expand-icon-class="text-white"
            :default-opened="false"
          >
            <q-card>
              <q-card-section v-if="feedsCount === 0" class="text-grey-7 text-center">
                No downstream equipment
              </q-card-section>
              <q-list v-else dense separator>
                <q-item v-for="fed in federEquipment" :key="`fed-${fed.id}`" dense>
                  <q-item-section avatar>
                    <q-icon :name="getEquipmentIcon(fed.brick_class)" :color="getEquipmentColor(fed.brick_class)" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ fed.name }}</q-item-label>
                    <q-item-label caption>{{ formatBrickClass(fed.brick_class) }}</q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-badge v-if="fed.rated_capacity" color="grey-7" outline>
                      {{ fed.rated_capacity }}A
                    </q-badge>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-card>
          </q-expansion-item>

          <!-- Controllers Section (for DDC panels) -->
          <q-expansion-item
            v-if="isDDCPanel"
            :label="`Controllers (${controllerCount})`"
            icon="settings_remote"
            header-class="bg-purple text-white"
            expand-icon-class="text-white"
            :default-opened="controllerCount > 0"
          >
            <q-card>
              <q-card-section v-if="controllerCount === 0" class="text-grey-7 text-center">
                No controllers configured
              </q-card-section>
              <q-list v-else dense separator>
                <q-item
                  v-for="controller in controllers"
                  :key="`controller-${controller.id}`"
                  dense
                  clickable
                  @click="emit('viewControllerPoints', controller)"
                >
                  <q-item-section avatar>
                    <q-icon name="settings_remote" color="purple" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ controller.name || controller.label || 'Unnamed Controller' }}</q-item-label>
                    <q-item-label caption>{{ formatBrickClass(controller.brick_class) }}</q-item-label>
                    <q-item-label caption class="text-grey-6">
                      {{ getControllerIoSummary(controller) }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side top>
                    <q-badge v-if="getControllerPointCount(controller) > 0" color="purple" text-color="white">
                      {{ getControllerPointCount(controller) }} pt{{ getControllerPointCount(controller) !== 1 ? 's' : '' }}
                    </q-badge>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-card>
          </q-expansion-item>

          <!-- Circuit Section (for Switchboards and Distribution Boards) -->
          <q-expansion-item
            v-if="isSwitchboard || isDistributionBoard"
            :label="`Circuits (${circuitCount})`"
            icon="cable"
            header-class="bg-indigo text-white"
            expand-icon-class="text-white"
            :default-opened="false"
          >
            <q-card>
              <q-card-section v-if="circuitCount === 0" class="text-grey-7 text-center">
                No circuits configured
              </q-card-section>
              <q-list v-else dense separator>
                <q-item v-for="circuit in circuits" :key="`circuit-${circuit.id}`" dense clickable @click="selectCircuit(circuit)">
                  <q-item-section avatar>
                    <q-icon name="cable" color="indigo" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ circuit.name }}</q-item-label>
                    <q-item-label caption>
                      {{ circuit.model_number ? `#${circuit.model_number}` : '' }}
                      {{ circuit.manufacturer ? `(${circuit.manufacturer})` : '' }}
                    </q-item-label>
                    <q-item-label v-if="circuit.rated_capacity" caption class="text-grey-6">
                      {{ circuit.rated_capacity }}A
                    </q-item-label>
                  </q-item-section>
                  <q-item-section v-if="circuit.children && circuit.children.length > 0" side>
                    <q-badge color="green" outline>
                      <q-icon name="speed" size="xs" class="q-mr-xs" />
                      {{ circuit.children.length }}
                    </q-badge>
                  </q-item-section>
                  <q-item-section side>
                    <q-btn
                      flat
                      dense
                      round
                      icon="more_vert"
                      size="sm"
                      @click.stop="showCircuitMenu(circuit, $event)"
                    >
                      <q-menu>
                        <q-list dense>
                          <q-item v-close-popup clickable @click="editCircuit(circuit)">
                            <q-item-section avatar>
                              <q-icon name="edit" />
                            </q-item-section>
                            <q-item-section>Edit Circuit</q-item-section>
                          </q-item>
                          <q-item v-close-popup clickable @click="addMeterToCircuit(circuit)">
                            <q-item-section avatar>
                              <q-icon name="speed" />
                            </q-item-section>
                            <q-item-section>Add Meter</q-item-section>
                          </q-item>
                          <q-separator />
                          <q-item v-close-popup clickable @click="deleteCircuit(circuit)">
                            <q-item-section avatar>
                              <q-icon name="delete" color="negative" />
                            </q-item-section>
                            <q-item-section>Delete</q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                  </q-item-section>
                </q-item>
              </q-list>

              <q-separator />

              <!-- Add Circuit Button -->
              <q-card-section class="q-pa-sm">
                <q-btn
                  flat
                  dense
                  color="primary"
                  icon="add"
                  label="Add Circuit"
                  class="full-width"
                  @click="addCircuit"
                />
              </q-card-section>
            </q-card>
          </q-expansion-item>

        </q-list>
      </div>
    </template>

    <!-- Add Meter Dialog -->
    <q-dialog v-model="showMeterDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="row items-center bg-primary text-white">
          <div class="text-h6">{{ meterDialogMode === 'edit' ? 'Edit Meter' : 'Add Meter to Circuit' }}</div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section>
          <q-form @submit="saveMeter">
            <q-input
              v-model="meterForm.name"
              label="Meter Name *"
              outlined
              dense
              class="q-mb-md"
              :rules="[val => !!val || 'Name is required']"
            />

            <q-select
              v-model="meterForm.brick_class"
              :options="meterTypes"
              label="Meter Type *"
              outlined
              dense
              class="q-mb-md"
              :rules="[val => !!val || 'Meter type is required']"
            />

            <q-input
              v-model="meterForm.manufacturer"
              label="Manufacturer"
              outlined
              dense
              class="q-mb-md"
            />

            <q-input
              v-model="meterForm.model_number"
              label="Model Number"
              outlined
              dense
              class="q-mb-md"
            />

            <q-input
              v-model="meterForm.description"
              label="Description"
              type="textarea"
              outlined
              dense
              rows="3"
            />

            <div class="row justify-end q-gutter-sm q-mt-md">
              <q-btn label="Cancel" color="grey" flat @click="showMeterDialog = false" />
              <q-btn
                label="Save"
                type="submit"
                color="primary"
                :loading="savingMeter"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Add/Edit Circuit Dialog -->
    <q-dialog v-model="showCircuitDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="row items-center bg-primary text-white">
          <div class="text-h6">{{ circuitDialogMode === 'add' ? 'Add Circuit' : 'Edit Circuit' }}</div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section>
          <q-form @submit="saveCircuit">
            <q-input
              v-model="circuitForm.name"
              label="Circuit Name *"
              outlined
              dense
              class="q-mb-md"
              :rules="[val => !!val || 'Name is required']"
            />

            <q-input
              v-model="circuitForm.circuit_number"
              label="Circuit Number"
              outlined
              dense
              class="q-mb-md"
              hint="e.g., C-101, 15A-1"
            />

            <q-input
              v-model="circuitForm.circuit_type"
              label="Circuit Type"
              outlined
              dense
              class="q-mb-md"
              hint="e.g., Lighting, Power, HVAC"
            />

            <q-input
              v-model.number="circuitForm.rated_load"
              type="number"
              label="Rated Load (A)"
              outlined
              dense
              class="q-mb-md"
              min="0"
              step="1"
            />

            <q-select
              v-model="circuitForm.phase_configuration"
              :options="['single', 'three-phase']"
              label="Phase Configuration"
              outlined
              dense
              class="q-mb-md"
            />

            <q-input
              v-model="circuitForm.description"
              label="Description"
              type="textarea"
              outlined
              dense
              rows="3"
            />

            <div class="row justify-end q-gutter-sm q-mt-md">
              <q-btn label="Cancel" color="grey" flat @click="showCircuitDialog = false" />
              <q-btn
                label="Save"
                type="submit"
                color="primary"
                :loading="savingCircuit"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { getBrickIcon, getBrickColor } from '@/composables/brick-schema/config/brickIconMappings';
  import { api } from '@/services/api';
  import { fetchControllerTemplate } from '@/services/controllerTemplateService';

  const props = defineProps({
    selectedEquipment: {
      type: Object,
      default: null,
    },
    hierarchyData: {
      type: Object,
      default: null,
    },
  });

  const emit = defineEmits(['refresh', 'select-equipment', 'viewControllerPoints']);

  // State
  const loading = ref(false);
  const controllerTemplatesCache = ref({});
  const editableProps = ref({
    rated_capacity: '',
    voltage: '',
    phase_configuration: '',
    manufacturer: '',
    model_number: '',
  });
  const showCircuitDialog = ref(false);
  const circuitDialogMode = ref('add'); // 'add' or 'edit'
  const savingCircuit = ref(false);
  const currentEditingCircuit = ref(null);
  const circuitForm = ref({
    name: '',
    circuit_number: '',
    circuit_type: '',
    rated_load: null,
    phase_configuration: 'single',
    description: '',
  });

  // Meter dialog state
  const showMeterDialog = ref(false);
  const savingMeter = ref(false);
  const meterDialogMode = ref('add'); // 'add' or 'edit'
  const currentCircuitForMeter = ref(null);
  const currentEditingMeter = ref(null);
  const meterForm = ref({
    name: '',
    brick_class: '',
    manufacturer: '',
    model_number: '',
    description: '',
  });

  const meterTypes = [
    'brick:Electrical_Meter',
    'brick:Energy_Meter',
    'brick:Power_Meter',
    'brick:Current_Meter',
    'brick:Voltage_Meter',
  ];

  // Computed - Equipment Type Checks
  const isSwitchboard = computed(() => {
    if (!props.selectedEquipment) {return false;}
    const brickClass = props.selectedEquipment.brick_class?.toLowerCase() || '';
    return brickClass.includes('switchboard') || brickClass.includes('breaker_panel');
  });

  const isDDCPanel = computed(() => {
    if (!props.selectedEquipment) {return false;}
    const brickClass = props.selectedEquipment.brick_class?.toLowerCase() || '';
    return brickClass.includes('ddc') && brickClass.includes('panel');
  });

  const isDistributionBoard = computed(() => {
    if (!props.selectedEquipment) {return false;}
    const brickClass = props.selectedEquipment.brick_class?.toLowerCase() || '';
    return brickClass.includes('distribution_board');
  });

  const isMeter = computed(() => {
    if (!props.selectedEquipment) {return false;}
    const brickClass = props.selectedEquipment.brick_class?.toLowerCase() || '';
    return brickClass.includes('meter');
  });

  // Computed - Child Equipment
  // Meters - load via API instead of from tree children
  const meters = ref([]);
  const meterCount = computed(() => meters.value.length);

  const federEquipment = computed(() => {
    if (!props.selectedEquipment?.children) {return [];}
    return props.selectedEquipment.children.filter((child) =>
      !child.brick_class?.toLowerCase().includes('meter'),
    );
  });

  const feedsCount = computed(() => federEquipment.value.length);

  const controllers = computed(() => {
    if (!props.selectedEquipment?.children) {return [];}
    const ctrs = props.selectedEquipment.children.filter((child) =>
      child.brick_class?.toLowerCase().includes('controller'),
    );

    // Load templates for controllers that have controller_template_id
    ctrs.forEach(async (controller) => {
      const templateId = controller.data?.controller_template_id || controller.controller_template_id;
      if (templateId && !controllerTemplatesCache.value[templateId]) {
        try {
          const template = await fetchControllerTemplate(templateId);
          controllerTemplatesCache.value[templateId] = template;
        } catch (error) {
          console.error(`Failed to load controller template ${templateId}:`, error);
        }
      }
    });

    return ctrs;
  });

  const controllerCount = computed(() => controllers.value.length);

  // Extract properties from selected equipment
  const equipmentProps = computed(() => getEquipmentProperties(props.selectedEquipment));

  // Circuits computed
  const circuits = ref([]);
  const circuitCount = computed(() => circuits.value.length);

  // Methods
  function getEquipmentIcon(brickClass) {
    return getBrickIcon(brickClass);
  }

  function getEquipmentColor(brickClass) {
    return getBrickColor(brickClass);
  }

  function formatBrickClass(brickClass) {
    if (!brickClass) {return 'Unknown';}
    return brickClass
      .replace('brick:', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  function getEquipmentType(brickClass) {
    if (!brickClass) {return 'Equipment';}
    const formatted = formatBrickClass(brickClass);
    // Remove common suffixes for cleaner display
    return formatted.replace(/ (Equipment|Device|System)$/i, '');
  }

  function getMonitoringTarget() {
    return 'Entire Board';
  }

  // Get controller I/O summary for display (e.g., "16 UI, 8 UO")
  function getControllerIoSummary(controller) {
    if (!controller) {return 'No I/O';}

    // Get template ID from controller data
    const templateId = controller.data?.controller_template_id || controller.controller_template_id;

    if (!templateId) {
      return 'No Template';
    }

    // Check if template is loaded in cache
    const template = controllerTemplatesCache.value[templateId];

    if (!template || !template.io_capacity) {
      return 'Loading...';
    }

    const capacity = template.io_capacity;
    const parts = [];

    // Show non-zero I/O types
    if (capacity.UI > 0) {parts.push(`${capacity.UI} UI`);}
    if (capacity.UO > 0) {parts.push(`${capacity.UO} UO`);}
    if (capacity.AI > 0) {parts.push(`${capacity.AI} AI`);}
    if (capacity.AO > 0) {parts.push(`${capacity.AO} AO`);}
    if (capacity.DI > 0) {parts.push(`${capacity.DI} DI`);}
    if (capacity.DO > 0) {parts.push(`${capacity.DO} DO`);}

    return parts.length > 0 ? parts.join(', ') : 'No I/O';
  }

  // Get controller point count (number of points currently allocated)
  function getControllerPointCount(controller) {
    if (!controller) {return 0;}

    // Count children that are points
    const pointCount = controller.children?.filter((child) =>
      child.brick_class?.toLowerCase().includes('point'),
    ).length || 0;

    return pointCount;
  }

  function selectMeter(meter) {
    emit('select-equipment', meter);
  }

  function showMeterMenu(meter, event) {
  // Menu is handled by q-menu in template
  }

  // Helper function to extract properties from equipment
  function getEquipmentProperties(equipment) {
    // Check for properties field first (from direct API responses)
    if (equipment?.properties) {
      return typeof equipment.properties === 'string'
        ? JSON.parse(equipment.properties)
        : equipment.properties;
    }

    // Fall back to data.properties (from tree nodes that wrap the original equipment)
    if (equipment?.data?.properties) {
      return typeof equipment.data.properties === 'string'
        ? JSON.parse(equipment.data.properties)
        : equipment.data.properties;
    }

    return {};
  }

  // Track the current equipment ID to detect actual equipment changes
  const currentEquipmentId = ref(null);

  // Watch for equipment changes and populate editable props
  watch(() => props.selectedEquipment, (newEquipment) => {
    if (newEquipment) {
      // Only update editableProps if we switched to a different equipment
      if (currentEquipmentId.value !== newEquipment.id) {
        currentEquipmentId.value = newEquipment.id;
        const equipProps = getEquipmentProperties(newEquipment);

        editableProps.value = {
          rated_capacity: equipProps.rated_capacity || equipProps.amperage || '',
          voltage: equipProps.voltage || '',
          phase_configuration: equipProps.phase_configuration || '',
          manufacturer: equipProps.manufacturer || '',
          model_number: equipProps.model_number || '',
        };
      }
    }
  }, { immediate: true, deep: true });

  // Inline property update
  async function updateEquipmentProperty(propertyName, value) {
    if (!props.selectedEquipment?.id) {return;}


    try {
      const payload = {};
      payload[propertyName] = value;

      const response = await fetch(`/api/equipment/${props.selectedEquipment.id}`, {
        method: 'PUT',
        headers: api.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to update equipment:', error);
        // Revert the change
        const equipProps = getEquipmentProperties(props.selectedEquipment);
        editableProps.value[propertyName] = equipProps[propertyName] || '';
        return;
      }

      // Get the updated equipment data from the response
      const result = await response.json();

      // Just keep the value that was just saved - don't overwrite with response
      // The value is already correct in editableProps since we're using v-model

      // Refresh the equipment data in parent
      emit('refresh');
    } catch (error) {
      console.error('Failed to update equipment:', error);
      // Revert the change
      const equipProps = getEquipmentProperties(props.selectedEquipment);
      editableProps.value[propertyName] = equipProps[propertyName] || '';
    }
  }

  function editMeter(meter) {
    const props = getEquipmentProperties(meter);

    // Populate form with meter data
    meterForm.value = {
      name: meter.name || '',
      brick_class: meter.brick_class || 'brick:Electrical_Meter',
      manufacturer: props.manufacturer || '',
      model_number: props.model_number || '',
      description: meter.description || '',
    };
    currentEditingMeter.value = meter;
    meterDialogMode.value = 'edit';
    currentCircuitForMeter.value = null; // Not adding to circuit, editing existing meter
    showMeterDialog.value = true;
  }

  async function deleteMeter(meter) {
    // Show confirmation dialog
    if (!confirm(`Are you sure you want to delete "${meter.name}"?`)) {
      return;
    }

    try {
      // Determine the parent (either switchboard or circuit)
      const parentId = meter.parent_equipment_id;

      // Find if this meter belongs to a circuit or directly to switchboard
      const circuit = circuits.value.find((c) => c.id === parentId);
      let endpoint;

      if (circuit && props.selectedEquipment?.id) {
        // Meter belongs to a circuit
        endpoint = `/api/switchboards/${props.selectedEquipment.id}/circuits/${circuit.id}/meters/${meter.id}`;
      } else if (props.selectedEquipment?.id) {
        // Meter belongs directly to switchboard
        endpoint = `/api/equipment/${props.selectedEquipment.id}/meters/${meter.id}`;
      } else {
        console.error('Cannot determine parent equipment');
        return;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: api.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to delete meter:', error);
        alert('Failed to delete meter');
        return;
      }

      // Reload meters and circuits
      await loadCircuits();
      await loadMeters();
    } catch (error) {
      console.error('Failed to delete meter:', error);
      alert('Failed to delete meter');
    }
  }

  function addMeter() {
  }

  // Circuit management methods
  async function loadCircuits() {
    if (!props.selectedEquipment?.id) {return;}

    try {
      const response = await fetch(`/api/switchboards/${props.selectedEquipment.id}/circuits`, {
        headers: api.getHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to load circuits:', response.status, response.statusText);
        return;
      }

      const data = await response.json();
      if (data.success) {
        circuits.value = data.circuits;
      }
    } catch (error) {
      console.error('Failed to load circuits:', error);
    }
  }

  async function loadMeters() {
    if (!props.selectedEquipment?.id) {return;}

    try {
      const response = await fetch(`/api/equipment/${props.selectedEquipment.id}/meters`, {
        headers: api.getHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to load meters:', response.status, response.statusText);
        return;
      }

      const data = await response.json();
      if (data.success) {
        meters.value = data.meters;
      }
    } catch (error) {
      console.error('Failed to load meters:', error);
    }
  }

  function selectCircuit(circuit) {
  }

  function showCircuitMenu(circuit, event) {
  // Menu is handled by q-menu in template
  }

  function addCircuit() {
    // Reset form
    circuitForm.value = {
      name: '',
      circuit_number: '',
      circuit_type: '',
      rated_load: null,
      phase_configuration: 'single',
      description: '',
    };
    circuitDialogMode.value = 'add';
    showCircuitDialog.value = true;
  }

  async function saveCircuit() {
    if (!props.selectedEquipment?.id) {return;}

    savingCircuit.value = true;

    try {
      let url, method;

      if (circuitDialogMode.value === 'edit' && currentEditingCircuit.value) {
        // Edit existing circuit
        url = `/api/switchboards/${props.selectedEquipment.id}/circuits/${currentEditingCircuit.value.id}`;
        method = 'PUT';
      } else {
        // Create new circuit
        url = `/api/switchboards/${props.selectedEquipment.id}/circuits`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: api.getHeaders(),
        body: JSON.stringify(circuitForm.value),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to save circuit:', error);
        return;
      }

      const data = await response.json();
      if (data.success) {
        showCircuitDialog.value = false;
        currentEditingCircuit.value = null;
        await loadCircuits();
      }
    } catch (error) {
      console.error('Failed to save circuit:', error);
    } finally {
      savingCircuit.value = false;
    }
  }

  function editCircuit(circuit) {
    // Populate form with circuit data from properties JSON field
    const props = typeof circuit.properties === 'string' ? JSON.parse(circuit.properties) : (circuit.properties || {});

    circuitForm.value = {
      name: circuit.name || '',
      circuit_number: props.circuit_number || '',
      circuit_type: props.circuit_type || '',
      rated_load: props.rated_load || null,
      phase_configuration: props.phase_configuration || 'single',
      description: circuit.description || '',
    };
    currentEditingCircuit.value = circuit;
    circuitDialogMode.value = 'edit';
    showCircuitDialog.value = true;
  }

  function addMeterToCircuit(circuit) {
    // Reset form
    meterForm.value = {
      name: '',
      brick_class: 'brick:Electrical_Meter',
      manufacturer: '',
      model_number: '',
      description: '',
    };
    currentCircuitForMeter.value = circuit;
    showMeterDialog.value = true;
  }

  async function saveMeter() {
    savingMeter.value = true;

    try {
      let response;

      if (meterDialogMode.value === 'edit') {
        // Update existing meter
        if (!currentEditingMeter.value?.id) {return;}

        response = await fetch(`/api/equipment/${currentEditingMeter.value.id}`, {
          method: 'PUT',
          headers: api.getHeaders(),
          body: JSON.stringify(meterForm.value),
        });
      } else {
        // Add new meter to circuit
        if (!props.selectedEquipment?.id || !currentCircuitForMeter.value?.id) {return;}

        response = await fetch(`/api/switchboards/${props.selectedEquipment.id}/circuits/${currentCircuitForMeter.value.id}/meters`, {
          method: 'POST',
          headers: api.getHeaders(),
          body: JSON.stringify(meterForm.value),
        });
      }

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to save meter:', error);
        alert('Failed to save meter');
        return;
      }

      const data = await response.json();
      if (data.success) {
        showMeterDialog.value = false;
        currentCircuitForMeter.value = null;
        currentEditingMeter.value = null;
        await loadCircuits();
        await loadMeters();
      }
    } catch (error) {
      console.error('Failed to save meter:', error);
      alert('Failed to save meter');
    } finally {
      savingMeter.value = false;
    }
  }

  async function deleteCircuit(circuit) {
    if (!confirm(`Are you sure you want to delete circuit "${circuit.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/switchboards/${props.selectedEquipment.id}/circuits/${circuit.id}`, {
        method: 'DELETE',
        headers: api.getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to delete circuit:', error);
        return;
      }

      const data = await response.json();
      if (data.success) {
        await loadCircuits();
      }
    } catch (error) {
      console.error('Failed to delete circuit:', error);
    }
  }

  // Watch for equipment selection changes
  watch(() => props.selectedEquipment, (newVal) => {
    if (newVal) {
      loading.value = false;
      // Load circuits and meters when switchboard is selected
      if (isSwitchboard.value || isDistributionBoard.value) {
        loadCircuits();
        loadMeters();
      }
    }
  });
</script>

<style lang="scss" scoped>
.electrical-sidebar {
  background: #f5f5f5;
  border-left: 1px solid #e0e0e0;
}

.expand-trigger {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateX(-50%) scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  .expand-text {
    text-transform: uppercase;
  }
}

.equipment-summary-card {
  margin: 40px 8px 0 8px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
}
</style>
